import { SessionStorageKeys } from "utils/storageKeys";

/**
 * Query parameters a page view must not carry to an analytics or advertising
 * vendor: the token on a sign-in link, an email verification or a password
 * reset, and the address an offer link prefills. They are read off the landing
 * URL once, here, and taken off the address bar.
 *
 * A secret the server has not accepted yet is also kept for the tab, so a reload
 * or a tab the phone restored still holds the link it arrived with.
 */

export const SECRET_PARAMS = ["token", "email"] as const;

type SecretParam = (typeof SECRET_PARAMS)[number];

type Secrets = Partial<Record<SecretParam, string>>;

let captured: Secrets = {};

export function captureUrlSecrets(
  location: Pick<Location, "href"> = window.location,
  history: Pick<History, "replaceState" | "state"> = window.history
) {
  const url = new URL(location.href);
  captured = readStored();
  let found = false;
  for (const name of SECRET_PARAMS) {
    const value = url.searchParams.get(name);
    if (value !== null) {
      captured[name] = value;
      url.searchParams.delete(name);
      found = true;
    }
  }
  if (found) {
    writeStored(captured);
    history.replaceState(history.state, "", url);
  }
}

// The value the landing URL carried for `name`.
export function urlSecret(name: SecretParam) {
  return captured[name]?.trim() ?? null;
}

// Called once the server has accepted the secret. The page keeps reading it until
// it unloads, and a reload then arrives without it.
export function markUrlSecretSpent(name: SecretParam) {
  const { [name]: _spent, ...rest } = readStored();
  writeStored(rest);
}

function readStored(): Secrets {
  try {
    const stored = window.sessionStorage.getItem(SessionStorageKeys.urlSecrets);
    return stored ? (JSON.parse(stored) as Secrets) : {};
  } catch {
    return {};
  }
}

function writeStored(secrets: Secrets) {
  try {
    if (Object.keys(secrets).length > 0) {
      window.sessionStorage.setItem(
        SessionStorageKeys.urlSecrets,
        JSON.stringify(secrets)
      );
    } else {
      window.sessionStorage.removeItem(SessionStorageKeys.urlSecrets);
    }
  } catch {
    // Storage can be unavailable in private browsing modes; the secret then lasts
    // until the page unloads.
  }
}
